/**
 * The readable guard on the figure (combat design docs/design/combat/2026-10-11-guard-read, approved by the user 10-11):
 * which frame a rig figure shows while guarding, when the guard is weak, when it breaks and while it stays broken.
 * Poses: motions/guardRead.ts (per weapon family). Cues on the body and the shield icon: passes/guardReadTell.ts.
 * Picture only: the rules are shared's (guard points, `brokenT`, stagger: systems/duelMatch.ts; the guard-drop recovery:
 * systems/duelGuardDrop.ts).
 *
 * - Guarding: the guard raised (0.1 s), held; a blow on it jars it back (0.18 s); moving, the guarded shuffle facing the
 *   foe; in a bout with the guard points at or under `GUARD_READ.weakShare`, the weak guard.
 * - Let go: the guard lowered into ready over the guard-drop recovery.
 * - A guard broken (any `guardBreak` / `guardShatter` hit): the figure flashed white with a dark outline on the first
 *   frame (held by the hit-stop, render/hitStop.ts), then the guard thrown open for the stagger.
 * - Broken in a bout (`brokenT`): standing, the open stance (arms wide, no guard) until the guard comes back.
 */
import { duelMatchOf, GUARD_READ, lockLookOf, screenHeading, type Entity, type World, type WorldEvent } from '@nine/shared';
import type { IsoCamera } from './camera';
import { knockReactFrame } from './knockReact';
import type { GuardFamily } from './motions/guardRead';
import { motionPhase, type MotionId, type Pose } from './motions';
import { FRAMES } from './motions/kit';
import { atRes, artX, artY, canvas, ctx2d, resOf } from './pixel';
import { strideFps } from './qinggongAnim';
import { LOOKS, OUTFITS, viewOf, type SpriteSet } from './sprites';

/** Seconds: the guard raised, a blow's jar, the white flash on a break, how long after a break the stagger pose holds. */
const RAISE = 0.1;
const JAR = 0.18;
const FLASH = 0.06;
const STAGGER_MAX = 1.5;
/** Farthest a target is faced from while guarding on the move (world units; render/guardWalk.ts). */
const FACE_RANGE = 12;
const WALK_RATE = 1.7;

interface Track {
  blocking: boolean;
  since: number;
  released: number;
  jarAt: number;
  brokeAt: number;
}
const tracks = new WeakMap<World, Map<number, Track>>();
function track(w: World, id: number): Track {
  let m = tracks.get(w);
  if (!m) tracks.set(w, (m = new Map()));
  let t = m.get(id);
  if (!t) m.set(id, (t = { blocking: false, since: -9, released: -9, jarAt: -9, brokeAt: -9 }));
  return t;
}

/** Called for every `hit` event (render/effects.ts): a blow on the guard, a guard broken. */
export function guardReadOnHit(ev: Extract<WorldEvent, { type: 'hit' }>, w: World): void {
  const o = ev.outcome as string;
  if (o === 'blocked') track(w, ev.tgtId).jarAt = w.time;
  else if (o === 'guardBreak' || o === 'guardShatter') track(w, ev.tgtId).brokeAt = w.time;
}

/** World time of the last guard broken on `id` (−9: none). */
export const guardBrokeAt = (w: World, id: number): number => tracks.get(w)?.get(id)?.brokeAt ?? -9;

/** A fighter's guard as the bout holds it (null outside a bout's fight). */
export function boutGuard(w: World, id: number): { share: number; brokenT: number } | null {
  const b = w.pvpBout;
  if (!b?.fight || (b.a !== id && b.b !== id)) return null;
  const v = duelMatchOf(w)?.fighterView(id);
  return v ? { share: v.gp / Math.max(1, v.gpMax), brokenT: v.brokenT } : null;
}

const FAMILY: Record<string, GuardFamily> = { sword: 'Blade', sabre: 'Sabre', staff: 'Staff', none: 'Fist' };
const famCache = new Map<string, GuardFamily>();
/** The guard family of a figure's weapon (its look and outfits, as the sprite bank dresses it). */
export function familyOf(e: { sprite: string; outfit?: readonly string[] }): GuardFamily {
  const key = `${e.sprite}|${e.outfit?.join(',') ?? ''}`;
  let f = famCache.get(key);
  if (!f) {
    let weapon: string = LOOKS[e.sprite]?.weapon ?? 'sword';
    for (const o of e.outfit ?? []) weapon = OUTFITS[o]?.weapon ?? weapon;
    f = FAMILY[weapon] ?? 'Blade';
    famCache.set(key, f);
  }
  return f;
}

const flashes = new WeakMap<HTMLCanvasElement, HTMLCanvasElement>();
/** The frame as a flat white silhouette with a one-art-pixel dark outline (reads on pale and dark ground alike). */
function flashOf(img: HTMLCanvasElement): HTMLCanvasElement {
  let c = flashes.get(img);
  if (c) return c;
  const r = resOf(img);
  const solid = (col: string) => {
    const s = canvas(img.width, img.height);
    const g = ctx2d(s);
    g.drawImage(img, 0, 0);
    g.globalCompositeOperation = 'source-in';
    g.fillStyle = col;
    g.fillRect(0, 0, s.width, s.height);
    return s;
  };
  c = canvas(img.width, img.height);
  const g = ctx2d(c);
  const dark = solid('#0e0a10');
  for (const [dx, dy] of [[-r, 0], [r, 0], [0, -r], [0, r]] as const) g.drawImage(dark, dx, dy);
  g.drawImage(solid('#fdf6ec'), 0, 0);
  atRes(c, r, artX(img), artY(img));
  flashes.set(img, c);
  return c;
}

export interface GuardShot {
  img: HTMLCanvasElement;
  facing?: number;
  dx?: number;
}

/**
 * The guard frame for `e`, or null when the guard has nothing to show (the renderer's own branches go on). `set` and
 * `facing` are the view the renderer picked; `base` the figure's own set (all its views).
 */
export function guardReadShot(
  w: World,
  e: Entity,
  set: SpriteSet,
  base: SpriteSet | undefined,
  iso: IsoCamera,
  facing: number,
): GuardShot | null {
  const extra = set.extra;
  if (!extra || e.dead) return null;
  const t = w.time;
  const tr = track(w, e.id);
  if (e.blocking !== tr.blocking) {
    if (e.blocking) tr.since = t;
    else tr.released = t;
    tr.blocking = e.blocking;
  }
  const f = familyOf(e);
  const still = (p: Pose) => extra(p, 0, false, () => set.block);
  const motion = (s: SpriteSet, id: MotionId, u: number) =>
    s.extra!('motion', motionPhase(id, Math.max(0, Math.min(1, u)) * (FRAMES - 1)), false, () => s.block);
  // A guard broken: the flash (held by the hit-stop), then the guard thrown open while staggered.
  const sinceBreak = t - tr.brokeAt;
  if (sinceBreak >= 0 && sinceBreak < STAGGER_MAX && e.stunT > 0 && !knockReactFrame(e.id)) {
    const img = still('grImpact');
    return { img: sinceBreak < FLASH ? flashOf(img) : img, dx: Math.round(Math.sin(e.animT * 30)) };
  }
  const bg = boutGuard(w, e.id);
  if (e.blocking) {
    if (t - tr.jarAt < JAR) return { img: motion(set, `grJar${f}`, (t - tr.jarAt) / JAR), dx: t - tr.jarAt < 0.08 ? -1 : 0 };
    if (e.moving) return walkShot(e, set, base, iso, facing, f, w);
    if (t - tr.since < RAISE) return { img: motion(set, `grUp${f}`, (t - tr.since) / RAISE) };
    return { img: still(bg && bg.share <= GUARD_READ.weakShare ? `grWeak${f}` : `grGuard${f}`) };
  }
  const idle = !e.moving && !e.cast && e.swingT <= 0 && e.dashT <= 0 && e.stunT <= 0 && e.hitT <= 0 && e.z <= 0.5;
  if (!idle) return null;
  const drop = GUARD_READ.dropFrames / 60 + 0.04;
  if (t - tr.released < drop) return { img: motion(set, `grDrop${f}`, (t - tr.released) / drop) };
  if (bg && bg.brokenT > 0) return { img: still('grOpen') };
  return null;
}

/** The guarded shuffle facing the target (render/guardWalk.ts's strafe, with this family's guard). */
function walkShot(
  e: Entity,
  set: SpriteSet,
  base: SpriteSet | undefined,
  iso: IsoCamera,
  facing: number,
  f: GuardFamily,
  w: World,
): GuardShot | null {
  let view = { set, facing };
  let back = false;
  const target = e.targetId !== null ? w.entities.get(e.targetId) : null;
  if (base && target && !target.dead && target !== e && !lockLookOf(w)?.lookOf(e.id)) {
    const dx = target.x - e.x;
    const dy = target.y - e.y;
    const l = Math.hypot(dx, dy);
    if (l > 0.01 && l < FACE_RANGE) {
      const h = iso.heading({ heading: screenHeading(dx, dy), dirX: dx / l, dirY: dy / l });
      view = viewOf(base, h, facing);
      back = ((e.dirX ?? 0) * dx + (e.dirY ?? 0) * dy) / l < -0.35;
    }
  }
  if (!view.set.extra) return null;
  const n = Math.abs(Math.floor(e.animT * strideFps(e, view.set) * WALK_RATE)) % FRAMES;
  const ph = motionPhase(`grWalk${f}`, back ? FRAMES - 1 - n : n);
  return { img: view.set.extra('motion', ph, false, () => view.set.block), facing: view.facing };
}
